import { useEffect, useState, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import DoneIcon from '@mui/icons-material/Done';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useStoreState, useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Loader from '~/ui/components/common/Loader';
import Card from './cards/Card';
import ProgramCard from './cards/ProgramCard';
import { UserRole, UserPermission } from '~/types';
import styles from './Triage.module.scss';
import { TriageType } from '~/services/api/enums';
import TriagePopup from './popups/TriagePopup';
import { ITriageProgramCounters } from '~/services/api/triage/types';
import TriageRequestPopup from './popups/TriageRequestPopup';
import RecentActivity from './RecentActivity';
import { IBloodPressureReading } from '~/services/api/readings/types';

const Triage = (): ReactElement => {
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedProgramId, setSelectedProgramId] = useState<number>(null);
  const [selectedTriageTypeId, setSelectedTriageTypeId] = useState<TriageType>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<number>(null);
  const [title, setTitle] = useState('');
  const [showTriagePopup, setShowTriagePopup] = useState(false);

  const userRole = useStoreState(state => state.auth.currentUser?.roleId);
  const userPermissions = useStoreState(state => state.auth.currentUser?.permissions || []);
  const { counters } = useStoreState(state => state.triage);
  const { items: auditLogs } = useStoreState(state => state.auditLogs);

  const { onGetTriageCounters, onUpdatedBloodPressureReading } = useStoreActions(
    actions => actions.triage,
  );
  const { onGetAuditLogs } = useStoreActions(actions => actions.auditLogs);
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const permissions = {
    canManagePatients:
      [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole) ||
      userPermissions.includes(UserPermission.ManagePatients),
  };

  const onBloodPressureUpdated = (data: IBloodPressureReading) => {
    onUpdatedBloodPressureReading(data);
  };

  const onMount = async () => {
    try {
      await onGetTriageCounters();
      await onGetAuditLogs({});

      setIsLoading(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onProgramClick = (programCounters: ITriageProgramCounters) => {
    setSelectedTriageTypeId(null);
    setSelectedProgramId(programCounters.id);
    setTitle(programCounters.name);
    setShowTriagePopup(true);
  };

  const onRequestsClick = (triageTypeId: TriageType) => {
    setSelectedProgramId(null);
    setSelectedTriageTypeId(triageTypeId);
    setTitle(triageTypeId === TriageType.WaitingReply ? 'Waiting Reply' : 'Completed');
    setShowTriagePopup(true);
  };

  const onTriagePopupClose = () => {
    setShowTriagePopup(false);
  };

  const onTriageRequestPopupClose = () => {
    setSelectedNoteId(null);
  };

  const onTriageRequestUpdated = () => {
    setSelectedNoteId(null);
    setShowTriagePopup(false);
    setSelectedProgramId(null);
    setSelectedTriageTypeId(null);
    setIsLoading(true);
    onMount();
  };

  useEffect(() => {
    const noteId = Number(new URLSearchParams(location.search).get('noteId'));

    if (noteId) {
      setSelectedNoteId(noteId);
    }
  }, [location]);

  useEffect(() => {
    onMount();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.dashboard}>
      {counters.programsCounters.length > 0 && (
        <div className={styles.row}>
          <div className={styles.title}>Triage Programs</div>
          <div className={styles.content}>
            {counters.programsCounters.map(programCounters => (
              <div key={programCounters.id} className={styles.contentItem}>
                <ProgramCard
                  amount={programCounters.count}
                  title={programCounters.name}
                  hasHighPriority={programCounters.hasHighPriority}
                  onClick={() => onProgramClick(programCounters)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={styles.row}>
        <div className={styles.title}>Requests</div>
        <div className={styles.content}>
          <div className={styles.contentItem}>
            <Card
              colorType="green"
              iconComponent={DoneIcon}
              amount={counters.completedRequestsCount}
              title="Completed"
              onClick={() => onRequestsClick(TriageType.Completed)}
            />
          </div>
          <div className={styles.contentItem}>
            <Card
              colorType="red"
              iconComponent={HourglassTopIcon}
              amount={counters.waitingReplyRequestsCount}
              title="Waiting Reply"
              onClick={() => onRequestsClick(TriageType.WaitingReply)}
            />
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.title}>Recent Activity</div>
        <RecentActivity auditLogs={auditLogs.slice(0, 9)} />
      </div>
      {showTriagePopup && (
        <TriagePopup
          permissions={permissions}
          programId={selectedProgramId}
          triageTypeId={selectedTriageTypeId}
          title={title}
          onClose={onTriagePopupClose}
          setSelectedNoteId={setSelectedNoteId}
          onBloodPressureUpdated={onBloodPressureUpdated}
        />
      )}
      {selectedNoteId != null && (
        <TriageRequestPopup
          noteId={selectedNoteId}
          onUpdated={onTriageRequestUpdated}
          onClose={onTriageRequestPopupClose}
        />
      )}
    </div>
  );
};

export default Triage;
